const KEY_PREFIX = "lbk1.";
const LEGACY_KEY_PREFIX = "lnk1.";
const VAULT_PREFIX = "lbv1.";
const ENVELOPE_PREFIX = "lb1.";
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });

const importedKeys = new Map();

export class SyncEncryptionError extends Error {
  constructor(message, code = "encryption_error") {
    super(message);
    this.name = "SyncEncryptionError";
    this.code = code;
  }
}

function bytesToBase64Url(bytes) {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function base64UrlToBytes(value) {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) {
    throw new SyncEncryptionError("加密密钥格式不正确", "invalid_key");
  }
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  try {
    const binary = atob(padded);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    throw new SyncEncryptionError("加密密钥格式不正确", "invalid_key");
  }
}

export function generateVaultKey() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return `${KEY_PREFIX}${bytesToBase64Url(bytes)}`;
}

export function normalizeVaultKey(value) {
  const normalized = value.trim();
  const prefix = [KEY_PREFIX, LEGACY_KEY_PREFIX].find((candidate) =>
    normalized.startsWith(candidate),
  );
  if (!prefix) {
    throw new SyncEncryptionError("加密密钥必须以 lbk1. 或 lnk1. 开头", "invalid_key");
  }
  const bytes = base64UrlToBytes(normalized.slice(prefix.length));
  if (bytes.length !== 32) {
    throw new SyncEncryptionError("加密密钥长度不正确", "invalid_key");
  }
  return normalized;
}

function rawKeyBytes(value) {
  const normalized = normalizeVaultKey(value);
  const prefix = normalized.startsWith(KEY_PREFIX) ? KEY_PREFIX : LEGACY_KEY_PREFIX;
  return base64UrlToBytes(normalized.slice(prefix.length));
}

async function importVaultKey(value) {
  const normalized = normalizeVaultKey(value);
  if (!importedKeys.has(normalized)) {
    importedKeys.set(
      normalized,
      crypto.subtle.importKey("raw", rawKeyBytes(normalized), { name: "AES-GCM" }, false, [
        "encrypt",
        "decrypt",
      ]),
    );
  }
  return importedKeys.get(normalized);
}

function additionalData(noteId) {
  return encoder.encode(`light-boat:note:v1:${noteId}`);
}

export async function deriveVaultId(value) {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", rawKeyBytes(value)));
  return `${VAULT_PREFIX}${bytesToBase64Url(digest.subarray(0, 18))}`;
}

export async function encryptNoteBody(body, noteId, vaultKey) {
  const key = await importVaultKey(vaultKey);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: nonce,
        additionalData: additionalData(noteId),
        tagLength: 128,
      },
      key,
      encoder.encode(body),
    ),
  );
  return `${ENVELOPE_PREFIX}${bytesToBase64Url(nonce)}.${bytesToBase64Url(ciphertext)}`;
}

export async function decryptNoteBody(envelope, noteId, vaultKey) {
  if (typeof envelope !== "string" || !envelope.startsWith(ENVELOPE_PREFIX)) {
    throw new SyncEncryptionError(
      "云端返回了未加密或不兼容的笔记数据；本地内容没有被修改",
      "unencrypted_remote_data",
    );
  }
  const parts = envelope.split(".");
  if (parts.length !== 3) {
    throw new SyncEncryptionError("云端密文格式损坏", "invalid_envelope");
  }
  const nonce = base64UrlToBytes(parts[1]);
  const ciphertext = base64UrlToBytes(parts[2]);
  if (nonce.length !== 12 || ciphertext.length < 16) {
    throw new SyncEncryptionError("云端密文格式损坏", "invalid_envelope");
  }

  try {
    const key = await importVaultKey(vaultKey);
    const plaintext = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: nonce,
        additionalData: additionalData(noteId),
        tagLength: 128,
      },
      key,
      ciphertext,
    );
    return decoder.decode(plaintext);
  } catch (error) {
    if (error instanceof SyncEncryptionError) throw error;
    throw new SyncEncryptionError(
      "无法解密云端笔记，请检查所有设备是否使用同一把加密密钥",
      "decryption_failed",
    );
  }
}
